/**
 * Redacted (privacy-mode) message classes.
 *
 * These wrap a normal transaction with the stealth + relayer context needed
 * to submit it through the redacted backend instead of broadcasting directly
 * from the user's wallet.
 *
 * Per Hans's review on MR 173: moved here from `exec.ts` so that the public
 * msg primitives stay separate from the redacted/privacy variants.
 */

import { Account } from "../accounts";
import { Asset } from "../asset";
import { BaseMsg, Msg } from "./msg";

/**
 * Base class for MsgRedacted classes that share the same parameters:
 * account, stealth, amount, denom
 */
export abstract class MsgRedactedBase extends BaseMsg implements Msg {
  constructor(
    public account: Account,
    public stealth: string,
    public amount: string,
    public denom: string
  ) {
    super();
  }
}

export class MsgRedactedGasTankWithdraw extends MsgRedactedBase {}

export class MsgRedactedWithdraw extends MsgRedactedBase {}

export class MsgRedactedDepositToSubWallet extends MsgRedactedBase {}

export class MsgRedactedWithdrawFromSubWallet extends MsgRedactedBase {}

export class MsgRedactedThorchainDeposit extends MsgRedactedBase {
  constructor(
    account: Account,
    stealth: string,
    amount: string,
    public asset: Asset,
    public memo: string
  ) {
    // Use the Asset type's native denom directly (Hans's review comment on
    // exec.ts:231 — prefer `.variants.native.denom` over the ad-hoc string
    // comparison). Falls back to empty string if the asset has no native
    // denom — callers passing a non-native asset to a redacted swap is a
    // bug and should be caught upstream.
    const denom = asset.variants?.native?.denom ?? "";
    super(account, stealth, amount, denom);
  }
}

/** V1-compatible specialization retained for the legacy backend router. */
export class MsgRedactedSwap extends MsgRedactedThorchainDeposit {}

export class MsgRedactedCall extends MsgRedactedBase {
  constructor(
    account: Account,
    stealth: string,
    amount: string,
    denom: string,
    public contractAddress: string,
    public call: string // json-stringified
  ) {
    super(account, stealth, amount, denom);
  }
}

export class MsgRedactedBatchCalls extends MsgRedactedBase {
  constructor(
    account: Account,
    stealth: string,
    public calls: unknown[] // canonical typed calls
  ) {
    super(account, stealth, "0", "");
  }
}
